import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, switchMap, throwError } from 'rxjs';
import { SpinnerService } from '../services/spinner.service';
import {
  ACCESS_TOKEN_KEY,
  ACCESS_TOKEN_REFRESH_KEY,
  AuthService,
} from '../services/auth.service';
import { environment } from '../../environments/environment';

/**
 * The two auth endpoints authenticate with the *Google* ID token in the standard
 * Authorization header, so they must be left alone - everything else is
 * authorised with the gateway's access token in X-Gars-Authorization.
 *
 * Matched against the resolved *pathname*, never with `url.includes(...)`:
 * a substring test would treat any URL merely containing this text - e.g.
 * `https://elsewhere.example/?next=/v1/auth/login` - as an auth endpoint and
 * silently drop the bearer header, failing open.
 */
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/local-token'];

/** Origin the gateway is served from. */
const API_ORIGIN = new URL(environment.apiUrl).origin;
const DIRECT_API_ORIGIN = (environment as any).directApiUrl
  ? new URL((environment as any).directApiUrl).origin
  : null;

/** Resolve a possibly-relative request URL to its pathname. */
const pathnameOf = (url: string): string | null => {
  try {
    return new URL(url, location.origin).pathname;
  } catch {
    return null;
  }
};

const isAuthPath = (url: string): boolean => {
  const pathname = pathnameOf(url);
  if (pathname === null) return false;
  return AUTH_PATHS.some((path) => pathname.endsWith(path) || pathname.includes(path));
};

const isProfilePath = (url: string): boolean => {
  const pathname = pathnameOf(url);
  return pathname !== null && (pathname.includes('/gars-users/profile') || pathname.includes('/auth/profile'));
};

/**
 * The access token is a bearer credential: anything that receives it can replay
 * it. Attach it only for the gateway (or same-origin relative URLs, or direct
 * backend service endpoints), never for an arbitrary third-party host.
 */
const shouldAttachToken = (url: string): boolean => {
  try {
    const origin = new URL(url, location.origin).origin;
    if (origin === API_ORIGIN || origin === location.origin) return true;
    if (DIRECT_API_ORIGIN && origin === DIRECT_API_ORIGIN) return true;
    if (origin.includes('gemini-report-300443444189') || origin.endsWith('.run.app')) return true;
    return false;
  } catch {
    return false;
  }
};

/**
 * How long a request must run before the loader is shown. The spinner is a
 * full-page overlay that blocks clicks, so putting it up for every request
 * makes the app flicker and feel sluggish on fast responses.
 */
const SPINNER_DELAY_MS = 300;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const loader = inject(SpinnerService);
  const authService = inject(AuthService);

  if (isAuthPath(req.url)) {
    // The login page renders its own state while SSO is in flight.
    return next(req);
  }

  const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const storedUser = authService.getStoredUser();
  const idToken = storedUser?.idToken;

  let cloned = req;
  if (shouldAttachToken(req.url)) {
    const headersToSet: Record<string, string> = {};
    if (accessToken && !req.headers.has('X-Gars-Authorization')) {
      headersToSet['X-Gars-Authorization'] = `Bearer ${accessToken}`;
    }
    // Set Authorization header if not already present (needed for Google Cloud Run / IAP)
    if (!req.headers.has('Authorization')) {
      const bearerToken = accessToken || idToken;
      if (bearerToken) {
        headersToSet['Authorization'] = `Bearer ${bearerToken}`;
      }
    }
    if (Object.keys(headersToSet).length > 0) {
      cloned = req.clone({ setHeaders: headersToSet });
    }
  }

  // Reference-counted in SpinnerService, so overlapping requests keep the
  // loader up until the last one settles. Pages that drive their own loading
  // state call setSuppressed(true) to opt out.
  let loaderRaised = false;
  const spinnerDelay = setTimeout(() => {
    loaderRaised = true;
    loader.show();
  }, SPINNER_DELAY_MS);

  return next(cloned).pipe(
    catchError((error) => {
      if (error?.status !== 401) return throwError(() => error);
      // Auth endpoints and user profile fetch must NEVER trigger token refresh
      if (isAuthPath(req.url) || isProfilePath(req.url)) {
        return throwError(() => error);
      }
      // If this request was already retried once after a refresh, do not loop
      if (req.headers.has('X-Auth-Retried')) {
        return throwError(() => error);
      }
      // If there is no refresh token stored yet, do not attempt refresh or force logout
      if (!localStorage.getItem(ACCESS_TOKEN_REFRESH_KEY)) return throwError(() => error);

      // Distinguishes "the refresh token is dead" (session over) from "this
      // endpoint rejected us" (session fine). Scoped to this request's own error
      // handler, so it cannot leak across concurrent requests.
      let refreshSucceeded = false;

      // The access token is no longer accepted. Trade the refresh token for a
      // new one and replay the original request exactly once.
      return authService.refreshAccessToken().pipe(
        switchMap((res) => {
          const newAccess = res?.data?.accessToken || localStorage.getItem(ACCESS_TOKEN_KEY);
          if (!newAccess) return throwError(() => error);

          refreshSucceeded = true;

          const retryHeaders: Record<string, string> = {
            'X-Gars-Authorization': `Bearer ${newAccess}`,
            'X-Auth-Retried': 'true',
          };
          if (!req.headers.has('Authorization')) {
            const bearerToken = newAccess || idToken;
            if (bearerToken) {
              retryHeaders['Authorization'] = `Bearer ${bearerToken}`;
            }
          }

          const retried = cloned.clone({
            setHeaders: retryHeaders,
          });
          return next(retried);
        }),
        catchError((refreshErr) => {
          // Refresh failed => the refresh token is gone or rejected and the
          // session cannot be recovered. End it here: otherwise the token pair
          // stays resident in localStorage and the user is stranded on a dead
          // page instead of being returned to SSO.
          //
          // Refresh succeeded but the retry still 401'd => the session is
          // genuinely valid and this endpoint is refusing us (403-style
          // authorisation failure). Propagate the error; signing out would
          // punish the user for an endpoint they were never allowed to call.
          if (!refreshSucceeded) authService.signOut();
          return throwError(() => refreshErr);
        }),
      );
    }),
    // Runs after the refresh-and-retry chain settles too, so the loader stays
    // up while the retry is still in flight.
    finalize(() => {
      clearTimeout(spinnerDelay);
      if (loaderRaised) loader.hide();
    }),
  );
};